
import { useState, useEffect } from 'react';
import Button from './ui/Button';
import LanguageSwitcher from './LanguageSwitcher';
import { Language } from '../types';

interface HeaderProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  isClient?: boolean;
  onNavigate?: (page: any) => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  onProjectsClick: () => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  t: {
    about: string;
    careers: string;
    investors: string;
    projects: string;
    adminPanel: string;
    logout: string;
    login: string;
    weLoveAndorra: string;
  };
  translationWarning?: string;
}

const HeartIcon = ({ className = '' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
  </svg>
);
const StarIcon = ({ className = '' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);


const Header = ({ 
  isLoggedIn, 
  isAdmin = false,
  isClient = false,
  onNavigate,
  onLoginClick, 
  onLogoutClick, 
  onProjectsClick, 
  language, 
  setLanguage, 
  t,
  translationWarning
}: HeaderProps) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isBannerClosing, setIsBannerClosing] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsBannerVisible(true);
    }, 5000);

    const hideTimer = setTimeout(() => {
      setIsBannerClosing(true);
    }, 65000); // 5s show delay + 60s visibility

    const removeTimer = setTimeout(() => {
      setIsBannerVisible(false);
    }, 66000); // Remove after 1s closing animation

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };
  
  return (
    <header className="w-full bg-slate-900/80 backdrop-blur-md border-b border-slate-800 pt-2 pb-4 md:pt-4 md:pb-6 sticky top-0 z-40">
      <div key="warning-container" className="empty:hidden">
        {language !== 'es' && translationWarning && (
          <div className="bg-red-600/90 text-white py-2 px-4 text-center text-[10px] md:text-sm font-bold border-b border-white/10 mb-4 animate-fade-in">
            {translationWarning}
          </div>
        )}
      </div>
      <div key="main-header-content" className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center z-50">
            <div className="relative flex items-center h-16 md:h-20">
              <button 
                onClick={() => onNavigate?.('home')}
                className="flex items-center cursor-pointer"
              >
                <div className="flex items-center">
                  <img 
                    src="https://datos.us/g/angelbadia.com/graphs/Logo_Negolan_Noviembre_2018.png" 
                    alt="Logo de Negolan" 
                    referrerPolicy="no-referrer"
                    className="h-10 md:h-14 w-auto object-contain shrink-0 scale-[5.5] md:scale-[7] origin-left -translate-x-14 md:-translate-x-40" 
                  />
                </div>
                <span className="absolute top-0 md:top-1 left-[138px] md:left-[235px] transform -rotate-[25deg] text-yellow-400 font-black text-xl md:text-4xl" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  3.0
                </span>
              </button>
              
              {isBannerVisible && (
                <div 
                  className={`absolute left-0 top-full mt-2 px-3 py-1.5 rounded-lg shadow-lg flex items-center space-x-1.5 bg-gradient-to-r from-blue-700 via-yellow-500 to-red-600 text-white font-bold whitespace-nowrap
                  ${isBannerClosing ? 'animate-slide-out-left' : 'animate-slide-in-left'}`}
                >
                  <StarIcon className="w-3 h-3 md:w-4 md:h-4 text-yellow-300 opacity-90" />
                  <span className="text-xs md:text-sm tracking-wide">{t.weLoveAndorra.split(' ')[0]}</span>
                  <HeartIcon className="w-4 h-4 md:w-5 md:h-5 text-rose-500 animate-heart-pulse" />
                  <span className="text-xs md:text-sm tracking-wide">{t.weLoveAndorra.split(' ')[2]}</span>
                  <StarIcon className="w-3 h-3 md:w-4 md:h-4 text-yellow-300 opacity-90" />
                </div>
              )}

            </div>
          </div>
          
          <div className="flex items-center gap-1 md:hidden z-50">
            <LanguageSwitcher language={language} setLanguage={setLanguage} className="scale-90 origin-right" />
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-300 hover:text-white p-1"
            >
              {isMobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>              
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              )}
            </button>
          </div>

          <nav className="hidden md:flex items-center space-x-6 text-slate-300">
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo('about'); }} className="hover:text-white transition-colors px-3 py-2 rounded-md text-sm font-medium">{t.about}</a>
            <a href="#careers" onClick={(e) => { e.preventDefault(); scrollTo('careers'); }} className="hover:text-white transition-colors px-3 py-2 rounded-md text-sm font-medium">{t.careers}</a>
            <Button onClick={() => scrollTo('investors')} variant="accent" className="px-4 py-2 text-sm">
              {t.investors}
            </Button>
          </nav>
          <div className="hidden md:flex items-center space-x-4">
             <Button onClick={onProjectsClick} variant="secondary" className="px-4 py-2 text-sm">
              {t.projects}
            </Button>
            {isLoggedIn ? (
              <>
                {isClient && (
                  <Button onClick={() => onNavigate?.('client-area')} variant="ghost" className="px-4 py-2 text-sm text-blue-400 hover:text-blue-300">
                    Mi Área
                  </Button>
                )}
                <Button onClick={onLogoutClick} variant="primary" className="px-4 py-2 text-sm">
                  {t.logout}
                </Button>
              </>
            ) : (
              <Button onClick={onLoginClick} variant="primary" className="px-4 py-2 text-sm">
                {t.login}
              </Button>
            )}
            <LanguageSwitcher language={language} setLanguage={setLanguage} />
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-b border-slate-800 shadow-xl py-4 px-4 flex flex-col space-y-4 z-40">
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo('about'); }} className="text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-md font-medium">{t.about}</a>
            <a href="#careers" onClick={(e) => { e.preventDefault(); scrollTo('careers'); }} className="text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-md font-medium">{t.careers}</a>
            <Button 
              onClick={() => {
                scrollTo('investors');
                setIsMobileMenuOpen(false);
              }} 
              variant="accent" 
              className="w-full justify-center"
            >
              {t.investors}
            </Button>
            <div className="h-px bg-slate-800 w-full my-2"></div>
            <Button onClick={() => { onProjectsClick(); setIsMobileMenuOpen(false); }} variant="secondary" className="w-full justify-center">
              {t.projects}
            </Button>
            {isLoggedIn ? (
              <>
                {isClient && (
                  <Button onClick={() => { onNavigate?.('client-area'); setIsMobileMenuOpen(false); }} variant="ghost" className="w-full justify-center text-blue-400">
                    Mi Área
                  </Button>
                )}
                <Button onClick={() => { onLogoutClick(); setIsMobileMenuOpen(false); }} variant="primary" className="w-full justify-center">
                  {t.logout}
                </Button>
              </>
            ) : (
              <Button onClick={() => { onLoginClick(); setIsMobileMenuOpen(false); }} variant="primary" className="w-full justify-center">
                {t.login}
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
